import { SupportMessage } from "@/components/layout-loading/support-message";
import { ButtonTeamBillingPortal } from "@/components/team/button-team-billing-portal";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { formatCurrency, formatDateNumeric } from "@/lib/format-amount";
import { TeamUnpaidInvoiceDTO } from "@shaarp/common";
import { ExternalLinkIcon } from "lucide-react";
import { ReactNode, useEffect } from "react";

type TeamBlockedUnpaidInvoiceProps = {
  /** The overdue invoice, shown to the roles that pay it. */
  readonly invoice: TeamUnpaidInvoiceDTO;
  /** Re-reads the team, so a payment made in another tab reopens the app. */
  readonly onPageShown: () => void;
};

/**
 * What the roles that pay see when an overdue invoice paid by transfer blocks
 * the team (#742): the invoice and its payment page. A member gets the generic
 * billing block instead (`ContentManager`), never told it is about an invoice.
 * Paying lifts the block on its own — nobody has to be called.
 */
export const TeamBlockedUnpaidInvoice = ({
  invoice,
  onPageShown,
}: TeamBlockedUnpaidInvoiceProps): ReactNode => {
  const { t, i18n } = useTranslation();
  const prefix = "setup.teamBlockedUnpaidInvoice";

  // The invoice is paid in another tab: coming back to this one re-reads the
  // team, so the app reopens as the screen promises, without a reload.
  useEffect(() => {
    const reloadWhenShown = (): void => {
      if (!document.hidden) onPageShown();
    };
    document.addEventListener("visibilitychange", reloadWhenShown);
    return () =>
      document.removeEventListener("visibilitychange", reloadWhenShown);
  }, [onPageShown]);

  return (
    <div className="flex-col py-6 space-y-3 text-center">
      <h1 className="text-2xl font-bold text-primary">{t(`${prefix}.title`)}</h1>
      <p>
        {t(`${prefix}.manager.line1`, {
          amount: formatCurrency(invoice.amountDue, invoice.currency, i18n.language),
          date: formatDateNumeric(invoice.dueAt, i18n.language),
        })}
        <br />
        {t(`${prefix}.manager.line2`)}
      </p>
      {invoice.hostedInvoiceUrl ? (
        <Button asChild>
          <a
            href={invoice.hostedInvoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t(`${prefix}.manager.payInvoice`)}
            <ExternalLinkIcon className="w-4 h-4" />
          </a>
        </Button>
      ) : (
        <ButtonTeamBillingPortal />
      )}
      <SupportMessage />
    </div>
  );
};
